// @flow
import React, { PureComponent } from "react";
import { connect } from "react-redux";
import type { Dispatch } from "redux";
import { bindAll, get, noop } from "lodash";
import withRouter from "@tvg/utils/withCustomRouter";
import type { RouterHistory } from "react-router-dom";
import type { NullaryFn } from "@tvg/types/Functional";
import type { TalentPickData } from "@tvg/types/TalentPicks";
import calculateTotals from "@tvg/api/wtx/BetHelper";
import mediator from "@tvg/mediator";
import tvgConf from "@tvg/conf";
import { mapSelectionsToString } from "@tvg/utils/talentPicksUtils";
import ModalV2 from "@fdr/atomic-ui/_templates/ModalV2";
import type { TitleType } from "@fdr/atomic-ui/_organisms/ModalHeader";
import BetConfirmation from "@fdr/atomic-ui/_templates/BetConfirmation";
import RaceAlerts from "@tvg/race-alerts";
import type {
  RaceProgram,
  RaceWagerType,
  BettingInterest
} from "@tvg/types/Race";
import {
  setBetConfirmation,
  setPickAction,
  setPickEditButtonAction,
  setShowRepeatBet,
  setBetTicketState,
  resetSelection,
  type Actions as BetActions
} from "@tvg/shared-actions/BetActions";
import {
  onTriggerDeposit,
  onTriggerLogin
} from "@tvg/sh-lib-account-wallet/src/utils/mobileUtils";
import { isMobile, isXSell } from "@tvg/sh-utils/mobileUtils";
import { clearSession } from "@tvg/sh-lib-account-wallet/src/session/logout";
import { type Actions as ProgramActions } from "@tvg/shared-actions/ProgramActions";
import type { GeolocationRejectedErrorMessage } from "@tvg/sh-geolocation/src/types";

import { getBetDetails } from "../utils";
import type { BetGroupStatus, OpenVideoAction } from "../actions";
import { setGroupBetTicketStatusClearAction } from "../actions";
import type { BetConfirmationAction } from "../reducers";

type Props = {
  dispatch: Dispatch<
    BetActions | ProgramActions | BetConfirmationAction | OpenVideoAction
  >,
  bettingInterests: Array<Array<BettingInterest>>,
  isOpen: boolean,
  isBetPlaced: boolean,
  isProcessing: boolean,
  isBettingError: boolean,
  isVideoShown: boolean,
  betAmount: number,
  betSelections: string[][],
  title: string,
  errorBetMessage: string,
  onRetain: NullaryFn<mixed>,
  onRetry: NullaryFn<mixed>,
  onBackToRace: NullaryFn<mixed>,
  onConfirmation: NullaryFn<mixed>,
  resetState: NullaryFn<mixed>,
  modalTopChildren: NullaryFn<mixed>,
  isNotificationsEnabled: boolean,
  race: RaceProgram,
  modalType: "bottom" | "fade",
  favoriteRunners: Array<Array<string>>,
  enableRepeatBetsPgmPage: boolean,
  enableRepeatBetsTalentPicks: boolean,
  talentPick: TalentPickData | null,
  pickEditButton: boolean,
  repetitionsNr: number,
  talentPickTriggerOrigin: ?("programpage" | "pickspage"),
  betGroupStatus?: BetGroupStatus,
  showLabelTag: boolean,
  forcedStop: boolean,
  hasVideo?: boolean,
  watchVideoActive?: boolean,
  selections: string[][],
  pgmDepositRedirectToggle?: boolean,
  errorBets: number,
  retryBetsToggle: boolean,
  scratchedSelectionMessage?: string,
  isSelectionScratched: boolean,
  onWarningClose?: NullaryFn<mixed>,
  history: RouterHistory,
  geolocationError?: GeolocationRejectedErrorMessage[],
  isBetDisabled?: boolean
};

export const getTitleType = (
  isBetPlaced: boolean,
  isBettingError: boolean
): TitleType => {
  if (isBetPlaced && !isBettingError) {
    return "success";
  }
  if (isBettingError) {
    return "error";
  }

  return "default";
};

const selectionsToStringReducer = (acc, value, index) =>
  get(value, "length", 0) ? `${acc}&s${index}=${value.toString()}` : acc;

export const selectionsToString = (selections: string[][]) =>
  selections.reduce(selectionsToStringReducer, "");

export const depositURL = ({
  selections,
  wagerType,
  betAmount
}: {
  selections: Array<Array<string>>,
  wagerType: string,
  betAmount: number
}) => {
  let callback = "";
  const stringParams = `wt=${wagerType}&bet=${betAmount}${selectionsToString(
    selections
  )}`;

  if (window) {
    callback = `${window.location.host}${
      window.location.pathname !== "/" ? window.location.pathname : ""
    }${window.location.search}${stringParams ? `&${stringParams}` : ""}`;
  }

  return tvgConf().buildUrl({
    app: "dma",
    path: "deposit",
    wrapper: true,
    callback: callback || false,
    params: { fullPage: true, more: true }
  });
};

export class BetConfirmationModalComponent extends PureComponent<Props> {
  static defaultProps = {
    isOpen: false,
    isBetPlaced: false,
    isProcessing: false,
    isBettingError: false,
    errorBetMessage: "",
    onConfirmation: noop,
    onRetain: noop,
    onRetry: noop,
    onBackToRace: noop,
    parseChannels: [],
    isNotificationsEnabled: false,
    modalType: "bottom",
    modalTopChildren: noop,
    favoriteRunners: [],
    bettingInterests: [],
    talentPick: null,
    talentPickTriggerOrigin: null,
    enableRepeatBetsPgmPage: false,
    enableRepeatBetsTalentPicks: false,
    pickEditButton: false,
    repetitionsNr: 1,
    betGroupStatus: {},
    showLabelTag: true,
    forcedStop: false,
    hasVideo: false,
    watchVideoActive: false,
    pgmDepositRedirectToggle: false,
    errorBets: 0,
    retryBetsToggle: false,
    insufficientFunds: false,
    scratchedSelectionMessage: "",
    isSelectionScratched: false,
    onWarningClose: noop,
    geolocationError: [],
    isBetDisabled: false
  };

  tvg: tvgConf;

  constructor(props: Props): void {
    super(props);
    bindAll(this, [
      "onClose",
      "onConfirmationHandler",
      "onRepeatModalOpen",
      "onResponsibleGamingError"
    ]);
    this.tvg = tvgConf();
  }

  componentDidUpdate(prevProps: Props) {
    if (!this.props.isOpen && prevProps.isOpen !== this.props.isOpen) {
      this.props.dispatch(setGroupBetTicketStatusClearAction());
    }
  }

  onConfirmationHandler() {
    if (this.props.talentPick) {
      mediator.base.dispatch({
        type: "TP_PROGRAMPAGE_CONFIRM_BET_OK",
        payload: {
          action: "Bet Confirm",
          label: `Talent Pick - ${get(this.props.talentPick, "talent.name")}`,
          talentPickOrigin: this.props.talentPickTriggerOrigin,
          tag: mapSelectionsToString(
            get(this.props.talentPick, "selections", [])
          ),
          race: {
            raceNumber: get(this.props.talentPick, "race"),
            track: {
              trackName: get(this.props.talentPick, "trackName")
            },
            type: {
              code: get(this.props.talentPick, "raceType")
            }
          },
          betAmount: get(this.props.talentPick, "amount"),
          betType: get(this.props.talentPick, "wagerType.abbreviation")
        }
      });
    } else {
      const bets = getBetDetails({
        betType: get(this.props, "betType"),
        selections: get(this.props, "betSelections", []),
        bettingInterests: get(this.props, "bettingInterests", []),
        betAmount: get(this.props, "betAmount", 0),
        betCount: 1
      });

      bets.forEach((bet) => {
        mediator.base.dispatch({
          type: "BET_CONFIRMATION:CONFIRM_BET",
          payload: {
            selections: get(bet, "selections").join(", "),
            trackName: get(this.props, "race.track.trackName"),
            raceNumber: get(this.props, "race.raceNumber"),
            betAmount: get(bet, "wagerAmount"),
            betType: get(bet, "wagerType.type.code"),
            runnerType:
              get(this.props, "race.type.code") === "G" ? "Greyhound" : "Horse"
          }
        });
      });
    }

    this.props.onConfirmation();
  }

  onRepeatModalOpen() {
    this.props.dispatch(setShowRepeatBet(true));
    mediator.base.dispatch({
      type: "REPEAT_BETS:LINK_CLICK",
      payload: {
        module: this.props.isBetPlaced
          ? "Bet Success Modal"
          : "Confirm Bet Modal",
        tag: this.props.talentPickTriggerOrigin ? "Talent" : undefined
      }
    });
  }

  onClose() {
    if (this.props.isBetPlaced && !this.props.isBettingError) {
      // the bet was placed - clean betting and remove bet ticket
      this.props.onBackToRace();

      mediator.base.dispatch({
        type: "BET_CONFIRMATION:CLOSE_CONFIRM_MODAL",
        payload: {
          confirmMode: false
        }
      });
    } else {
      const buttonActionType = get(
        this.props,
        "errorBetMessage.buttonType",
        "retry"
      );

      // when bet is not placed hide betConfirmation
      this.props.dispatch(setBetConfirmation(false));
      this.props.dispatch(setBetTicketState({ repeatBetNumber: 1 }));
      if (
        this.props.retryBetsToggle &&
        this.props.isBettingError &&
        buttonActionType !== "deposit"
      ) {
        this.props.dispatch(resetSelection());
      }
      setTimeout(() => this.props.resetState(), 400);

      if (this.props.talentPick) {
        mediator.base.dispatch({
          type: "TP_PROGRAMPAGE_CONFIRM_BET_CLOSE",
          payload: {
            action: "Close",
            label: "TP Confirm Bet Modal"
          }
        });
      } else {
        mediator.base.dispatch({
          type: "BET_CONFIRMATION:CLOSE_CONFIRM_MODAL",
          payload: {
            confirmMode: true
          }
        });
      }
    }

    if (this.props.talentPick) {
      this.props.dispatch(setPickAction(null));
      this.props.dispatch(setPickEditButtonAction(true));
    }
  }

  onResponsibleGamingError() {
    this.onClose();
    mediator.base.dispatch({
      type: "OPEN_AW_PAGE",
      payload: { path: "/responsible-play" }
    });
  }

  render() {
    const isSpecialGroup = get(this.props, "wagerType.specialGroup", false);

    const totals =
      this.props.isOpen && get(this.props, "wagerType.columnCount")
        ? calculateTotals(
            get(this.props, "betAmount"),
            get(this.props, "betType"),
            isSpecialGroup
              ? get(this.props, "betSelections.allSelections")
              : get(this.props, "betSelections")
          )
        : { betCount: 0 };

    const hasRaceAlertsBar =
      isMobile(this.tvg.product) &&
      this.props.isBetPlaced &&
      !isXSell(tvgConf.product);

    const baseProps = {
      title: this.props.title,
      titleType: getTitleType(
        this.props.isBetPlaced,
        this.props.isBettingError
      ),
      hasShadow: !this.props.isBetPlaced,
      animation: this.props.modalType,
      isOpen: this.props.isOpen,
      onClose: this.onClose,
      isFullHeight: false,
      qaLabel: "modal-betConfirmation",
      key: "betConfirmationModal",
      modalTopChildren: this.props.modalTopChildren,
      hasClose: !this.props.isProcessing,
      hasSafeArea: false
    };

    let extraProps = {
      isFullWidth: false,
      offsetTop: 0
    };

    if (this.props.modalType === "bottom") {
      extraProps = {
        hasVideoOffset: this.props.isVideoShown
      };
    }

    if (this.props.isProcessing) {
      extraProps = {
        ...extraProps,
        onOverlayClick: noop
      };
    }

    const props = { ...baseProps, ...extraProps };

    const wagerTypesSelected = getBetDetails({
      betType: get(this.props, "betType"),
      selections: get(this.props, "betSelections", []),
      bettingInterests: get(this.props, "bettingInterests", []),
      betAmount: get(this.props, "betAmount", 0),
      betCount: get(totals, "betCount", 1)
    });

    const openRaceVideo = () => {
      // $FlowFixMe
      this.props.dispatch({ type: "VIDEO_OPEN" });
      this.props.onBackToRace();
    };

    const onInsufficientFunds = () => {
      this.onClose();
      onTriggerDeposit();
    };

    const onExpiredToken = () => {
      const { history, dispatch, geolocationError } = this.props;
      // $FlowFixMe
      clearSession({ history, dispatch, geolocationError });
      onTriggerLogin();
    };

    return (
      <ModalV2 {...props}>
        {() => (
          <div>
            <BetConfirmation
              postTime={get(this.props, "race.postTime")}
              isProcessing={this.props.isProcessing}
              mtp={get(this.props, "race.mtp")}
              raceNumber={get(this.props, "race.raceNumber")}
              trackName={get(this.props, "race.track.trackName")}
              isGreyhound={get(this.props, "race.type.code") === "G"}
              raceTypeCode={get(this.props, "race.type.code")}
              wagerTypesSelected={wagerTypesSelected}
              betTotal={this.props.betAmount * totals.betCount}
              wagerAmount={this.props.betAmount}
              isCancelable={false}
              favoriteRunners={this.props.favoriteRunners}
              onConfirmation={this.onConfirmationHandler}
              onRetry={this.props.onRetry}
              onRetain={this.props.onRetain}
              onBackToRace={this.props.onBackToRace}
              isBetPlaced={this.props.isBetPlaced}
              isBettingError={this.props.isBettingError}
              errorBetMessage={this.props.errorBetMessage}
              hasRaceAlertsBar={hasRaceAlertsBar}
              pgmDepositRedirectToggle={this.props.pgmDepositRedirectToggle}
              confirmText="Confirm $amount Bet"
              betGroupStatus={this.props.betGroupStatus}
              showTag={wagerTypesSelected.length > 1 && !this.props.isBetPlaced}
              forcedStop={
                wagerTypesSelected.length > 1 && this.props.forcedStop
              }
              errorBets={this.props.errorBets}
              renderRaceAlerts={() => (
                <RaceAlerts
                  raceTrackCode={get(this.props, "race.track.trackCode", "")}
                  raceNumber={get(this.props, "race.raceNumber", "")}
                  racePostTime={get(this.props, "race.postTime", "")}
                  module="Bet Success Modal"
                  tvgRaceId={get(this.props, "race.tvgRaceId")}
                />
              )}
              hasVideo={this.props.hasVideo}
              watchVideoActive={this.props.watchVideoActive}
              onOpenVideo={openRaceVideo}
              retryBetsToggle={this.props.retryBetsToggle}
              isSelectionScratched={this.props.isSelectionScratched}
              onWarningClose={this.props.onWarningClose}
              scratchedSelectionMessage={this.props.scratchedSelectionMessage}
              onInsufficientFunds={onInsufficientFunds}
              isBetDisabled={this.props.isBetDisabled}
              {...(isXSell()
                ? { onExpiredToken }
                : { onResponsibleGamingError: this.onResponsibleGamingError })}
            />
          </div>
        )}
      </ModalV2>
    );
  }
}

export default connect(
  (
    store,
    props: {
      race: RaceProgram,
      wagerType: RaceWagerType,
      // $FlowFixMe
      betSelections: string[][],
      betAmount: number,
      showBetConfirmation: boolean,
      isVideoShown: boolean,
      modalTopChildren: NullaryFn<mixed>,
      bettingInterests: Array<Array<BettingInterest>>
    }
  ) => ({
    race: props.race,
    bettingInterests: props.bettingInterests,
    betType: props.wagerType,
    betAmount: props.betAmount,
    showBetConfirmation: props.showBetConfirmation,
    modalTopChildren: props.modalTopChildren,
    betSelections: props.betSelections,
    isVideoShown: props.isVideoShown,
    enableRepeatBetsPgmPage: get(
      store,
      "capi.featureToggles.enableRepeatBetsPgmPage"
    ),
    enableRepeatBetsTalentPicks: get(
      store,
      "capi.featureToggles.enableRepeatBetsTalentPicks"
    ),
    talentPick: get(store, "RaceProgram.talentPick", null),
    talentPickTriggerOrigin: get(
      store,
      "RaceProgram.talentPickTriggerOrigin",
      null
    ),
    pickEditButton: get(store, "RaceProgram.pickEditButton", null),
    pgmDepositRedirectToggle: get(
      store,
      "capi.featureToggles.pgmDepositRedirect"
    ),
    retryBetsToggle: get(store, "capi.featureToggles.retryBets", false),
    geolocationError: get(store, "geolocation.geolocationError", {})
  }),
  (dispatch: Dispatch<BetActions | ProgramActions>) => ({ dispatch })
)(withRouter(BetConfirmationModalComponent));
